import { Station } from '@app/core/types/stations';
import {
  AuditSpotAuditSpotMatchesListQueryParams,
  auditSpotAuditSpotMatchesListQueryKey,
  useAuditSpotAuditSpotMatchesList,
} from '@mi-front/api-v2';
import { DateTimeStr, TimeRange } from '@mi-front/core';
import { isAuditEnabled } from '../../../helpers';
import { AuditMatch } from '../types';
import { datetimeRangeCrudFilters } from '../../../utils/audit-matches-crud';

/**
 * Builds CRUD list filters for spot matches in a datetime range (optional customId).
 */
function spotMatchesQueryParams(
  station: Station,
  range: TimeRange<DateTimeStr>,
  options?: { size?: number; customId?: string | null },
): AuditSpotAuditSpotMatchesListQueryParams {
  return {
    station: station.id,
    filter: [
      ...datetimeRangeCrudFilters(range.from, range.to),
      ...(options?.customId ? [`customId||$eq||${options.customId}`] : []),
    ],
    limit: options?.size ?? 250,
  };
}

/**
 * Fetches spot matches for a station time range via api-v2 CRUD list.
 * Returns page `data` items (api-v2 DTOs) so consumers share the React Query cache.
 */
export function useAuditSpotMatches(
  station: Station,
  range: TimeRange<DateTimeStr>,
  options?: {
    size?: number;
    customId?: string | null;
  },
) {
  const params = spotMatchesQueryParams(station, range, options);

  return useAuditSpotAuditSpotMatchesList(params, {
    query: {
      enabled: isAuditEnabled(station) && (options?.customId === undefined || !!options.customId),
      // OpenAPI types matchedSchedule* as strings; runtime returns schedule objects / numbers.
      select: (res): AuditMatch[] => res.data as unknown as AuditMatch[],
    },
  });
}

useAuditSpotMatches.queryKey = (
  station: Station,
  range: TimeRange<DateTimeStr>,
  options?: { size?: number; customId?: string | null },
) => auditSpotAuditSpotMatchesListQueryKey(spotMatchesQueryParams(station, range, options));
